import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, BaseModelObject, ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumAlcoholType } from "./enums/enumAlcoholType.generated";
import { EnumGermanBeerDutyProductType } from "./enums/enumGermanBeerDutyProductType.generated";
import { EnumItemMainPurpose } from "./enums/enumItemMainPurpose.generated";
import { EnumSpiritDutyExemptionReason } from "./enums/enumSpiritDutyExemptionReason.generated";

// Model Objects

export class Behaviours extends ModelObject<Behaviours> {

    /**
    * True if this item is invoicable, therefore able to appear on orders / credits.
    */
    public isInvoiceable: boolean = false;
    /**
    * True if this item is stock tracked.
    */
    public isStockTracked: boolean = false;
    /**
    * True if this item is priced using the standard price lists and discounts structure. Typically all invoiceable items except those which have flat pricing.
    */
    public isPricedByPriceLists: boolean = false;
    /**
    * True if this item is a physical item.
    */
    public isPhysical: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.isInvoiceable = jsonSerializedObject.isInvoiceable;
          this.isStockTracked = jsonSerializedObject.isStockTracked;
          this.isPricedByPriceLists = jsonSerializedObject.isPricedByPriceLists;
          this.isPhysical = jsonSerializedObject.isPhysical;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.isInvoiceable = this.isInvoiceable;
        toRet.isStockTracked = this.isStockTracked;
        toRet.isPricedByPriceLists = this.isPricedByPriceLists;
        toRet.isPhysical = this.isPhysical;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class Details extends ModelObject<Details> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    public isHidden: boolean = false;
    /**
    * Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    /**
    * Code
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public code: string = "";
    /**
    * Main purpose - mainly for grouping items for management. Immutable
    */
    public mainPurpose: EnumItemMainPurpose = EnumItemMainPurpose.unknown;
    /**
    * Notes.
    * Not Nullable
    * Max Length : 100000
    */
    public notes: string = "";
    /**
    * Unit of Measure. Immutable
    */
    public unitOfMeasureId: Guid = Guid.createEmpty();
    /**
    * Calculated (ReadOnly) behaviours
    */
    public behaviours: Behaviours = new Behaviours();
    /**
    * Cost Price - Used for GrossProfit calculations when goods are sold.
    * Not Negative
    */
    public costPrice: number = 0;
    /**
    * Alternative Cost Price.
    * Not Negative
    */
    public alternativeCostPrice: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.isHidden = jsonSerializedObject.isHidden;
          this.name = jsonSerializedObject.name;
          this.code = jsonSerializedObject.code;
          this.mainPurpose = EnumItemMainPurpose.getByValue(jsonSerializedObject.mainPurpose, this.mainPurpose);
          this.notes = jsonSerializedObject.notes;
          this.unitOfMeasureId = Guid.parseOrEmpty(jsonSerializedObject.unitOfMeasureId);
          this.behaviours = new Behaviours(jsonSerializedObject.behaviours);
          this.costPrice = jsonSerializedObject.costPrice;
          this.alternativeCostPrice = jsonSerializedObject.alternativeCostPrice;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.isHidden = this.isHidden;
        toRet.name = this.name;
        toRet.code = this.code;
        toRet.mainPurpose = this.mainPurpose.value;
        toRet.notes = this.notes;
        toRet.unitOfMeasureId = this.unitOfMeasureId.toString();
        toRet.behaviours = this.behaviours.toJsonSerializedObject();
        toRet.costPrice = this.costPrice;
        toRet.alternativeCostPrice = this.alternativeCostPrice;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "Details.Id cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "code", rule: (v: any) => v == null || v.length !== 0 || "Code cannot be empty"},
            {field: "code", rule: (v: any) => v == null || v.length <= 1000 || "Code has too many characters (1000)"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "unitOfMeasureId", rule: (v: any) => !v.isEmpty() || "Details.UnitOfMeasureId cannot be empty"},
            {field: "behaviours", rule: (v: any) => !!v || "behaviours is required"},
            {field: "behaviours", rule: (v: any) => v.validate()},
    ];
}


export class Invoicing extends ModelObject<Invoicing> {

    /**
    * To indicate that Urn should be captured when printing labels or using in onward assemblies.
    */
    public deliveryBatchTracked: boolean = false;
    /**
    * Are sales currently allowed for this product? (Use to prevent selling of Eg out-of-season products).
    */
    public allowSales: boolean = false;
    private _productBrandId: Guid | null = null;
    /**
    * Product Brand Id. Optional even for Products.
    */
    public get productBrandId(): Guid | null { return this._productBrandId }
    public set productBrandId(value: Guid | null) {
      this._productBrandId = value === undefined ? null : value;
    }
    private _pricingCategoryId: Guid | null = null;
    /**
    * Pricing Category Id. Required for Products.
    */
    public get pricingCategoryId(): Guid | null { return this._pricingCategoryId }
    public set pricingCategoryId(value: Guid | null) {
      this._pricingCategoryId = value === undefined ? null : value;
    }
    /**
    * Gl Code
    */
    public salesGlCodeId: Guid = Guid.createEmpty();
    /**
    * Country Of Origin
    * Not Nullable
    * Max Length : 1000
    */
    public countryOfOrigin: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.deliveryBatchTracked = jsonSerializedObject.deliveryBatchTracked;
          this.allowSales = jsonSerializedObject.allowSales;
          this.productBrandId = jsonSerializedObject.productBrandId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.productBrandId);
          this.pricingCategoryId = jsonSerializedObject.pricingCategoryId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.pricingCategoryId);
          this.salesGlCodeId = Guid.parseOrEmpty(jsonSerializedObject.salesGlCodeId);
          this.countryOfOrigin = jsonSerializedObject.countryOfOrigin;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.deliveryBatchTracked = this.deliveryBatchTracked;
        toRet.allowSales = this.allowSales;
        toRet.productBrandId = this.productBrandId == null ? null : this.productBrandId.toString();
        toRet.pricingCategoryId = this.pricingCategoryId == null ? null : this.pricingCategoryId.toString();
        toRet.salesGlCodeId = this.salesGlCodeId.toString();
        toRet.countryOfOrigin = this.countryOfOrigin;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "productBrandId", rule: (v: any) => v == null || !v.isEmpty() || "Invoicing.ProductBrandId cannot be empty"},
            {field: "pricingCategoryId", rule: (v: any) => v == null || !v.isEmpty() || "Invoicing.PricingCategoryId cannot be empty"},
            {field: "salesGlCodeId", rule: (v: any) => !v.isEmpty() || "Invoicing.SalesGlCodeId cannot be empty"},
            {field: "countryOfOrigin", rule: (v: any) => v == null || v.length <= 1000 || "CountryOfOrigin has too many characters (1000)"},
    ];
}


export class Labelling extends ModelObject<Labelling> {

    /**
    * Use the Product Brand Tasting Notes
    */
    public labelUsesBrandTastingNotes: boolean = false;
    /**
    * Tasting Notes (May be overridden by branding).
    * Not Nullable
    * Max Length : 100000
    */
    public labelTastingNotes: string = "";
    /**
    * Use the Product Brand Ingredients and Allergens
    */
    public labelUsesBrandIngredientsAndAllergens: boolean = false;
    /**
    * Ingredients and Allergens
    * Not Nullable
    * Max Length : 100000
    */
    public labelIngredientsAndAllergens: string = "";
    private _bestBeforeDays: number | null = null;
    /**
    * Best before days. Used for pre-populating label's best before date.
    */
    public get bestBeforeDays(): number | null { return this._bestBeforeDays }
    public set bestBeforeDays(value: number | null) {
      this._bestBeforeDays = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.labelUsesBrandTastingNotes = jsonSerializedObject.labelUsesBrandTastingNotes;
          this.labelTastingNotes = jsonSerializedObject.labelTastingNotes;
          this.labelUsesBrandIngredientsAndAllergens = jsonSerializedObject.labelUsesBrandIngredientsAndAllergens;
          this.labelIngredientsAndAllergens = jsonSerializedObject.labelIngredientsAndAllergens;
          this.bestBeforeDays = jsonSerializedObject.bestBeforeDays == null ? null : jsonSerializedObject.bestBeforeDays;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.labelUsesBrandTastingNotes = this.labelUsesBrandTastingNotes;
        toRet.labelTastingNotes = this.labelTastingNotes;
        toRet.labelUsesBrandIngredientsAndAllergens = this.labelUsesBrandIngredientsAndAllergens;
        toRet.labelIngredientsAndAllergens = this.labelIngredientsAndAllergens;
        toRet.bestBeforeDays = this.bestBeforeDays == null ? null : this.bestBeforeDays;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "labelTastingNotes", rule: (v: any) => v == null || v.length <= 100000 || "LabelTastingNotes has too many characters (100000)"},
            {field: "labelIngredientsAndAllergens", rule: (v: any) => v == null || v.length <= 100000 || "LabelIngredientsAndAllergens has too many characters (100000)"},
            {field: "bestBeforeDays", rule: (v: any) => v == null || v >= -2147483648 || "BestBeforeDays is not greater than minimum allowed value: -2147483648"},
            {field: "bestBeforeDays", rule: (v: any) => v == null || v <= 2147483647 || "BestBeforeDays is above maximum allowed value: 2147483647"},
    ];
}


export class Packaging extends ModelObject<Packaging> {

    private _packageTypeId: Guid | null = null;
    /**
    * Package Type Id. Optional even for Products
    */
    public get packageTypeId(): Guid | null { return this._packageTypeId }
    public set packageTypeId(value: Guid | null) {
      this._packageTypeId = value === undefined ? null : value;
    }
    private _netWeightKg: number | null = null;
    /**
    * Net Weight (kg), Ie if this item is a packaged thing such as a foodstuff then the actual weight of the foodstuff within the package.
    * Not Negative
    */
    public get netWeightKg(): number | null { return this._netWeightKg }
    public set netWeightKg(value: number | null) {
      this._netWeightKg = value === undefined ? null : value;
    }
    /**
    * Weight (kg)
    * Not Negative
    */
    public weightKg: number = 0;
    private _exportWeight: number | null = null;
    /**
    * Export Weight, if different to standard weight.
    * Not Negative
    */
    public get exportWeight(): number | null { return this._exportWeight }
    public set exportWeight(value: number | null) {
      this._exportWeight = value === undefined ? null : value;
    }
    private _produceMaterialId: Guid | null = null;
    /**
    * Id of the produce Item (if any) which this product is a packaged version of. (Eg a product of Best Bitter 500ml Bottle is a package of the produce item Best Bitter.)
    */
    public get produceMaterialId(): Guid | null { return this._produceMaterialId }
    public set produceMaterialId(value: Guid | null) {
      this._produceMaterialId = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.packageTypeId = jsonSerializedObject.packageTypeId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.packageTypeId);
          this.netWeightKg = jsonSerializedObject.netWeightKg == null ? null : jsonSerializedObject.netWeightKg;
          this.weightKg = jsonSerializedObject.weightKg;
          this.exportWeight = jsonSerializedObject.exportWeight == null ? null : jsonSerializedObject.exportWeight;
          this.produceMaterialId = jsonSerializedObject.produceMaterialId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.produceMaterialId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.packageTypeId = this.packageTypeId == null ? null : this.packageTypeId.toString();
        toRet.netWeightKg = this.netWeightKg == null ? null : this.netWeightKg;
        toRet.weightKg = this.weightKg;
        toRet.exportWeight = this.exportWeight == null ? null : this.exportWeight;
        toRet.produceMaterialId = this.produceMaterialId == null ? null : this.produceMaterialId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "packageTypeId", rule: (v: any) => v == null || !v.isEmpty() || "Packaging.PackageTypeId cannot be empty"},
            {field: "produceMaterialId", rule: (v: any) => v == null || !v.isEmpty() || "Packaging.ProduceMaterialId cannot be empty"},
    ];
}


export class Stock extends ModelObject<Stock> {

    /**
    * Stock Group Id
    */
    public stockGroupId: Guid = Guid.createEmpty();
    /**
    * Is this item perishable.
    */
    public isPerishable: boolean = false;
    /**
    * Is Tracked in Batches.
    */
    public isBatchTracked: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.stockGroupId = Guid.parseOrEmpty(jsonSerializedObject.stockGroupId);
          this.isPerishable = jsonSerializedObject.isPerishable;
          this.isBatchTracked = jsonSerializedObject.isBatchTracked;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.stockGroupId = this.stockGroupId.toString();
        toRet.isPerishable = this.isPerishable;
        toRet.isBatchTracked = this.isBatchTracked;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "stockGroupId", rule: (v: any) => !v.isEmpty() || "Stock.StockGroupId cannot be empty"},
    ];
}


export class Vat extends ModelObject<Vat> {

    /**
    * Vat Code Id
    */
    public vatCodeId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.vatCodeId = Guid.parseOrEmpty(jsonSerializedObject.vatCodeId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.vatCodeId = this.vatCodeId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "vatCodeId", rule: (v: any) => !v.isEmpty() || "Vat.VatCodeId cannot be empty"},
    ];
}


export class AlcoholDuty extends ModelObject<AlcoholDuty> {

    /**
    * Alcohol Type.
    */
    public alcoholType: EnumAlcoholType = EnumAlcoholType.none;
    /**
    * Abv.
    * Not Negative
    * Maximum : 100
    */
    public abv: number = 0;
    private _dutiableLitres: number | null = null;
    /**
    * Dutiable Litres
    * Not Negative
    */
    public get dutiableLitres(): number | null { return this._dutiableLitres }
    public set dutiableLitres(value: number | null) {
      this._dutiableLitres = value === undefined ? null : value;
    }
    private _manufacturerId: Guid | null = null;
    /**
    * Manufacturer.
    */
    public get manufacturerId(): Guid | null { return this._manufacturerId }
    public set manufacturerId(value: Guid | null) {
      this._manufacturerId = value === undefined ? null : value;
    }
    private _plato: number | null = null;
    /**
    * Plato Value
    */
    public get plato(): number | null { return this._plato }
    public set plato(value: number | null) {
      this._plato = value === undefined ? null : value;
    }
    public germanBeerDutyProductType: EnumGermanBeerDutyProductType = EnumGermanBeerDutyProductType.none;
    private _germanBeerMixedProductPercentageA: number | null = null;
    public get germanBeerMixedProductPercentageA(): number | null { return this._germanBeerMixedProductPercentageA }
    public set germanBeerMixedProductPercentageA(value: number | null) {
      this._germanBeerMixedProductPercentageA = value === undefined ? null : value;
    }
    /**
    * Set true if this item is brewed by members of the public for their personal use by borrowing the Brewery's equipment. This is a service offered by breweries in some countries, such as Australia, and can affect duty.
    */
    public isBrewedForPersonalUse: boolean = false;
    public spiritDutyExemptionReason: EnumSpiritDutyExemptionReason = EnumSpiritDutyExemptionReason.none;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.alcoholType = EnumAlcoholType.getByValue(jsonSerializedObject.alcoholType, this.alcoholType);
          this.abv = jsonSerializedObject.abv;
          this.dutiableLitres = jsonSerializedObject.dutiableLitres == null ? null : jsonSerializedObject.dutiableLitres;
          this.manufacturerId = jsonSerializedObject.manufacturerId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.manufacturerId);
          this.plato = jsonSerializedObject.plato == null ? null : jsonSerializedObject.plato;
          this.germanBeerDutyProductType = EnumGermanBeerDutyProductType.getByValue(jsonSerializedObject.germanBeerDutyProductType, this.germanBeerDutyProductType);
          this.germanBeerMixedProductPercentageA = jsonSerializedObject.germanBeerMixedProductPercentageA == null ? null : jsonSerializedObject.germanBeerMixedProductPercentageA;
          this.isBrewedForPersonalUse = jsonSerializedObject.isBrewedForPersonalUse;
          this.spiritDutyExemptionReason = EnumSpiritDutyExemptionReason.getByValue(jsonSerializedObject.spiritDutyExemptionReason, this.spiritDutyExemptionReason);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.alcoholType = this.alcoholType.value;
        toRet.abv = this.abv;
        toRet.dutiableLitres = this.dutiableLitres == null ? null : this.dutiableLitres;
        toRet.manufacturerId = this.manufacturerId == null ? null : this.manufacturerId.toString();
        toRet.plato = this.plato == null ? null : this.plato;
        toRet.germanBeerDutyProductType = this.germanBeerDutyProductType.value;
        toRet.germanBeerMixedProductPercentageA = this.germanBeerMixedProductPercentageA == null ? null : this.germanBeerMixedProductPercentageA;
        toRet.isBrewedForPersonalUse = this.isBrewedForPersonalUse;
        toRet.spiritDutyExemptionReason = this.spiritDutyExemptionReason.value;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "manufacturerId", rule: (v: any) => v == null || !v.isEmpty() || "AlcoholDuty.ManufacturerId cannot be empty"},
    ];
}


export class PurchasingDetailReadOnly extends ModelObject<PurchasingDetailReadOnly> {

    /**
    * Stock item this puchasing details belongs to.
    */
    public itemId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.itemId = Guid.parseOrEmpty(jsonSerializedObject.itemId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.itemId = this.itemId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "itemId", rule: (v: any) => !v.isEmpty() || "PurchasingDetailReadOnly.ItemId cannot be empty"},
    ];
}


export class PurchasingDetails extends ModelObject<PurchasingDetails> {

    private _supplierId: Guid | null = null;
    /**
    * Unique, nullable suppplier id of the item. Null represents all other suppliers
    */
    public get supplierId(): Guid | null { return this._supplierId }
    public set supplierId(value: Guid | null) {
      this._supplierId = value === undefined ? null : value;
    }
    /**
    * Whether this supplier is ite's default supplier.
    */
    public isDefault: boolean = false;
    /**
    * Supplier Part Number
    * Not Nullable
    * Max Length : 10000
    */
    public supplierPartNo: string = "";
    private _purchasePrice: number | null = null;
    /**
    * Purchase Pack Price
    * Not Negative
    */
    public get purchasePrice(): number | null { return this._purchasePrice }
    public set purchasePrice(value: number | null) {
      this._purchasePrice = value === undefined ? null : value;
    }
    private _purchasePackSize: number | null = null;
    /**
    * Supplier Pack Size
    * Not Negative
    * Greater Than : 0
    */
    public get purchasePackSize(): number | null { return this._purchasePackSize }
    public set purchasePackSize(value: number | null) {
      this._purchasePackSize = value === undefined ? null : value;
    }
    private _purchaseGlCodeId: Guid | null = null;
    /**
    * Purchase GL Code
    */
    public get purchaseGlCodeId(): Guid | null { return this._purchaseGlCodeId }
    public set purchaseGlCodeId(value: Guid | null) {
      this._purchaseGlCodeId = value === undefined ? null : value;
    }
    /**
    * Number of days typically between ordering and receiving item.
    * Not Negative
    */
    public leadTimeDays: number = 0;
    public readOnly: PurchasingDetailReadOnly = new PurchasingDetailReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.supplierId = jsonSerializedObject.supplierId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.supplierId);
          this.isDefault = jsonSerializedObject.isDefault;
          this.supplierPartNo = jsonSerializedObject.supplierPartNo;
          this.purchasePrice = jsonSerializedObject.purchasePrice == null ? null : jsonSerializedObject.purchasePrice;
          this.purchasePackSize = jsonSerializedObject.purchasePackSize == null ? null : jsonSerializedObject.purchasePackSize;
          this.purchaseGlCodeId = jsonSerializedObject.purchaseGlCodeId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.purchaseGlCodeId);
          this.leadTimeDays = jsonSerializedObject.leadTimeDays;
          this.readOnly = new PurchasingDetailReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.supplierId = this.supplierId == null ? null : this.supplierId.toString();
        toRet.isDefault = this.isDefault;
        toRet.supplierPartNo = this.supplierPartNo;
        toRet.purchasePrice = this.purchasePrice == null ? null : this.purchasePrice;
        toRet.purchasePackSize = this.purchasePackSize == null ? null : this.purchasePackSize;
        toRet.purchaseGlCodeId = this.purchaseGlCodeId == null ? null : this.purchaseGlCodeId.toString();
        toRet.leadTimeDays = this.leadTimeDays;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "supplierId", rule: (v: any) => v == null || !v.isEmpty() || "PurchasingDetails.SupplierId cannot be empty"},
            {field: "supplierPartNo", rule: (v: any) => v == null || v.length <= 10000 || "SupplierPartNo has too many characters (10000)"},
            {field: "purchasePackSize", rule: (v: any) => v == null || v > 0 || "PurchasePackSize is not greater than minimum allowed value: 0"},
            {field: "purchaseGlCodeId", rule: (v: any) => v == null || !v.isEmpty() || "PurchasingDetails.PurchaseGlCodeId cannot be empty"},
            {field: "leadTimeDays", rule: (v: any) => v == null || v >= 0 || "LeadTimeDays is not greater than minimum allowed value: 0"},
            {field: "leadTimeDays", rule: (v: any) => v == null || v <= 2147483647 || "LeadTimeDays is above maximum allowed value: 2147483647"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class ExternalCodes extends ModelObject<ExternalCodes> {

    /**
    * EAN 13/14 Product Code
    * Not Nullable
    * Max Length : 100000
    */
    public eanBarcodeNumber: string = "";
    /**
    * Export Commodity Code
    * Not Nullable
    * Max Length : 1000
    */
    public exportCommodityCode: string = "";
    /**
    * Harmonized System (HS) Codes
    * Not Nullable
    * Max Length : 1000
    */
    public hsCode: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.eanBarcodeNumber = jsonSerializedObject.eanBarcodeNumber;
          this.exportCommodityCode = jsonSerializedObject.exportCommodityCode;
          this.hsCode = jsonSerializedObject.hsCode;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.eanBarcodeNumber = this.eanBarcodeNumber;
        toRet.exportCommodityCode = this.exportCommodityCode;
        toRet.hsCode = this.hsCode;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "eanBarcodeNumber", rule: (v: any) => v == null || v.length <= 100000 || "EanBarcodeNumber has too many characters (100000)"},
            {field: "exportCommodityCode", rule: (v: any) => v == null || v.length <= 1000 || "ExportCommodityCode has too many characters (1000)"},
            {field: "hsCode", rule: (v: any) => v == null || v.length <= 1000 || "HsCode has too many characters (1000)"},
    ];
}


export class FlatPricing extends ModelObject<FlatPricing> {

    public flatSalesPrice: number = 0;
    /**
    * Determines if the Flat Sales price, if defined, is gross (includes VAT) or Net (of VAT).
    */
    public isFlatSalesPriceGross: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.flatSalesPrice = jsonSerializedObject.flatSalesPrice;
          this.isFlatSalesPriceGross = jsonSerializedObject.isFlatSalesPriceGross;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.flatSalesPrice = this.flatSalesPrice;
        toRet.isFlatSalesPriceGross = this.isFlatSalesPriceGross;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class MaterialDetails extends ModelObject<MaterialDetails> {

    private _defaultHopAlpha: number | null = null;
    /**
    * If not null, then this is the default Hop Alpha. If the actual alpha of a specific batch is given, then ingredient usages will scale accoring to the ratio.
    * Not Negative
    */
    public get defaultHopAlpha(): number | null { return this._defaultHopAlpha }
    public set defaultHopAlpha(value: number | null) {
      this._defaultHopAlpha = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.defaultHopAlpha = jsonSerializedObject.defaultHopAlpha == null ? null : jsonSerializedObject.defaultHopAlpha;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.defaultHopAlpha = this.defaultHopAlpha == null ? null : this.defaultHopAlpha;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class AutoAssemblyBomLineReadOnly extends ModelObject<AutoAssemblyBomLineReadOnly> {

    /**
    * Auto assembled item which this line is a part of.
    */
    public parentAutoAssembledItemId: Guid = Guid.createEmpty();
    /**
    * Sequence for presenting Bom Lines in consistent order.
    */
    public sequence: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.parentAutoAssembledItemId = Guid.parseOrEmpty(jsonSerializedObject.parentAutoAssembledItemId);
          this.sequence = jsonSerializedObject.sequence;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.parentAutoAssembledItemId = this.parentAutoAssembledItemId.toString();
        toRet.sequence = this.sequence;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "parentAutoAssembledItemId", rule: (v: any) => !v.isEmpty() || "AutoAssemblyBomLineReadOnly.ParentAutoAssembledItemId cannot be empty"},
            {field: "sequence", rule: (v: any) => v == null || v >= -2147483648 || "Sequence is not greater than minimum allowed value: -2147483648"},
            {field: "sequence", rule: (v: any) => v == null || v <= 2147483647 || "Sequence is above maximum allowed value: 2147483647"},
    ];
}


export class AutoAssemblyBomLine extends ModelObject<AutoAssemblyBomLine> {

    /**
    * Item which is a component of the auto assembled item.
    */
    public itemId: Guid = Guid.createEmpty();
    /**
    * The quantity of Item required for each ParentAutoAssembledItem created.
    * Greater Than : 0
    */
    public quantity: number = 0;
    public readOnly: AutoAssemblyBomLineReadOnly = new AutoAssemblyBomLineReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.itemId = Guid.parseOrEmpty(jsonSerializedObject.itemId);
          this.quantity = jsonSerializedObject.quantity;
          this.readOnly = new AutoAssemblyBomLineReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.itemId = this.itemId.toString();
        toRet.quantity = this.quantity;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "itemId", rule: (v: any) => !v.isEmpty() || "AutoAssemblyBomLine.ItemId cannot be empty"},
            {field: "quantity", rule: (v: any) => v == null || v > 0 || "Quantity is not greater than minimum allowed value: 0"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class ItemFilter extends ModelObject<ItemFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _mainPurposes: EnumItemMainPurpose[] | null = null;
    public get mainPurposes(): EnumItemMainPurpose[] | null { return this._mainPurposes }
    public set mainPurposes(value: EnumItemMainPurpose[] | null) {
      this._mainPurposes = value === undefined ? null : value;
    }
    private _stockGroupIds: Guid[] | null = null;
    public get stockGroupIds(): Guid[] | null { return this._stockGroupIds }
    public set stockGroupIds(value: Guid[] | null) {
      this._stockGroupIds = value === undefined ? null : value;
    }
    private _salesGlCodeIds: Guid[] | null = null;
    public get salesGlCodeIds(): Guid[] | null { return this._salesGlCodeIds }
    public set salesGlCodeIds(value: Guid[] | null) {
      this._salesGlCodeIds = value === undefined ? null : value;
    }
    private _vatCodeIds: Guid[] | null = null;
    public get vatCodeIds(): Guid[] | null { return this._vatCodeIds }
    public set vatCodeIds(value: Guid[] | null) {
      this._vatCodeIds = value === undefined ? null : value;
    }
    private _codes: string[] | null = null;
    public get codes(): string[] | null { return this._codes }
    public set codes(value: string[] | null) {
      this._codes = value === undefined ? null : value;
    }
    private _itemIds: Guid[] | null = null;
    public get itemIds(): Guid[] | null { return this._itemIds }
    public set itemIds(value: Guid[] | null) {
      this._itemIds = value === undefined ? null : value;
    }
    private _productBrandIds: Guid[] | null = null;
    public get productBrandIds(): Guid[] | null { return this._productBrandIds }
    public set productBrandIds(value: Guid[] | null) {
      this._productBrandIds = value === undefined ? null : value;
    }
    private _packageTypeIds: Guid[] | null = null;
    public get packageTypeIds(): Guid[] | null { return this._packageTypeIds }
    public set packageTypeIds(value: Guid[] | null) {
      this._packageTypeIds = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.mainPurposes = jsonSerializedObject.mainPurposes == null ? null : jsonSerializedObject.mainPurposes.map((v: any) => EnumItemMainPurpose.getByValue(v));
          this.stockGroupIds = jsonSerializedObject.stockGroupIds == null ? null : jsonSerializedObject.stockGroupIds.map((v: any) => Guid.parseOrEmpty(v));
          this.salesGlCodeIds = jsonSerializedObject.salesGlCodeIds == null ? null : jsonSerializedObject.salesGlCodeIds.map((v: any) => Guid.parseOrEmpty(v));
          this.vatCodeIds = jsonSerializedObject.vatCodeIds == null ? null : jsonSerializedObject.vatCodeIds.map((v: any) => Guid.parseOrEmpty(v));
          this.codes = jsonSerializedObject.codes == null ? null : jsonSerializedObject.codes.map((v: any) => v);
          this.itemIds = jsonSerializedObject.itemIds == null ? null : jsonSerializedObject.itemIds.map((v: any) => Guid.parseOrEmpty(v));
          this.productBrandIds = jsonSerializedObject.productBrandIds == null ? null : jsonSerializedObject.productBrandIds.map((v: any) => Guid.parseOrEmpty(v));
          this.packageTypeIds = jsonSerializedObject.packageTypeIds == null ? null : jsonSerializedObject.packageTypeIds.map((v: any) => Guid.parseOrEmpty(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.mainPurposes = this.mainPurposes == null ? null : this.mainPurposes.map(v => v.value);
        toRet.stockGroupIds = this.stockGroupIds == null ? null : this.stockGroupIds.map(v => v.toString());
        toRet.salesGlCodeIds = this.salesGlCodeIds == null ? null : this.salesGlCodeIds.map(v => v.toString());
        toRet.vatCodeIds = this.vatCodeIds == null ? null : this.vatCodeIds.map(v => v.toString());
        toRet.codes = this.codes == null ? null : this.codes.map(v => v);
        toRet.itemIds = this.itemIds == null ? null : this.itemIds.map(v => v.toString());
        toRet.productBrandIds = this.productBrandIds == null ? null : this.productBrandIds.map(v => v.toString());
        toRet.packageTypeIds = this.packageTypeIds == null ? null : this.packageTypeIds.map(v => v.toString());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
    ];
}


export class Item extends BaseModelObject<Item> {

    public details: Details = new Details();
    private _invoicing: Invoicing | null = null;
    public get invoicing(): Invoicing | null { return this._invoicing }
    public set invoicing(value: Invoicing | null) {
      this._invoicing = value === undefined ? null : value;
    }
    public packaging: Packaging = new Packaging();
    private _stock: Stock | null = null;
    public get stock(): Stock | null { return this._stock }
    public set stock(value: Stock | null) {
      this._stock = value === undefined ? null : value;
    }
    private _vat: Vat | null = null;
    public get vat(): Vat | null { return this._vat }
    public set vat(value: Vat | null) {
      this._vat = value === undefined ? null : value;
    }
    private _alcoholDuty: AlcoholDuty | null = null;
    public get alcoholDuty(): AlcoholDuty | null { return this._alcoholDuty }
    public set alcoholDuty(value: AlcoholDuty | null) {
      this._alcoholDuty = value === undefined ? null : value;
    }
    public purchasingDetails: PurchasingDetails[] = [];
    public externalCodes: ExternalCodes = new ExternalCodes();
    private _flatPricing: FlatPricing | null = null;
    public get flatPricing(): FlatPricing | null { return this._flatPricing }
    public set flatPricing(value: FlatPricing | null) {
      this._flatPricing = value === undefined ? null : value;
    }
    public labelling: Labelling = new Labelling();
    private _materialDetails: MaterialDetails | null = null;
    public get materialDetails(): MaterialDetails | null { return this._materialDetails }
    public set materialDetails(value: MaterialDetails | null) {
      this._materialDetails = value === undefined ? null : value;
    }
    private _autoAssemblyBom: AutoAssemblyBomLine[] | null = null;
    public get autoAssemblyBom(): AutoAssemblyBomLine[] | null { return this._autoAssemblyBom }
    public set autoAssemblyBom(value: AutoAssemblyBomLine[] | null) {
      this._autoAssemblyBom = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.details = new Details(jsonSerializedObject.details);
          this.invoicing = jsonSerializedObject.invoicing == null ? null : new Invoicing(jsonSerializedObject.invoicing);
          this.packaging = new Packaging(jsonSerializedObject.packaging);
          this.stock = jsonSerializedObject.stock == null ? null : new Stock(jsonSerializedObject.stock);
          this.vat = jsonSerializedObject.vat == null ? null : new Vat(jsonSerializedObject.vat);
          this.alcoholDuty = jsonSerializedObject.alcoholDuty == null ? null : new AlcoholDuty(jsonSerializedObject.alcoholDuty);
          this.purchasingDetails = jsonSerializedObject.purchasingDetails.map((v: any) => new PurchasingDetails(v));
          this.externalCodes = new ExternalCodes(jsonSerializedObject.externalCodes);
          this.flatPricing = jsonSerializedObject.flatPricing == null ? null : new FlatPricing(jsonSerializedObject.flatPricing);
          this.labelling = new Labelling(jsonSerializedObject.labelling);
          this.materialDetails = jsonSerializedObject.materialDetails == null ? null : new MaterialDetails(jsonSerializedObject.materialDetails);
          this.autoAssemblyBom = jsonSerializedObject.autoAssemblyBom == null ? null : jsonSerializedObject.autoAssemblyBom.map((v: any) => new AutoAssemblyBomLine(v));
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.details = this.details.toJsonSerializedObject();
        toRet.invoicing = this.invoicing == null ? null : this.invoicing?.toJsonSerializedObject() ?? null;
        toRet.packaging = this.packaging.toJsonSerializedObject();
        toRet.stock = this.stock == null ? null : this.stock?.toJsonSerializedObject() ?? null;
        toRet.vat = this.vat == null ? null : this.vat?.toJsonSerializedObject() ?? null;
        toRet.alcoholDuty = this.alcoholDuty == null ? null : this.alcoholDuty?.toJsonSerializedObject() ?? null;
        toRet.purchasingDetails = this.purchasingDetails.map(v => v.toJsonSerializedObject());
        toRet.externalCodes = this.externalCodes.toJsonSerializedObject();
        toRet.flatPricing = this.flatPricing == null ? null : this.flatPricing?.toJsonSerializedObject() ?? null;
        toRet.labelling = this.labelling.toJsonSerializedObject();
        toRet.materialDetails = this.materialDetails == null ? null : this.materialDetails?.toJsonSerializedObject() ?? null;
        toRet.autoAssemblyBom = this.autoAssemblyBom == null ? null : this.autoAssemblyBom.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "details", rule: (v: any) => !!v || "details is required"},
            {field: "details", rule: (v: any) => v.validate()},
            {field: "invoicing", rule: (v: any) => (v === null) || v.validate()},
            {field: "packaging", rule: (v: any) => !!v || "packaging is required"},
            {field: "packaging", rule: (v: any) => v.validate()},
            {field: "stock", rule: (v: any) => (v === null) || v.validate()},
            {field: "vat", rule: (v: any) => (v === null) || v.validate()},
            {field: "alcoholDuty", rule: (v: any) => (v === null) || v.validate()},
            {field: "purchasingDetails", rule: (v: any) => !!v || "purchasingDetails is required"},
            {field: "purchasingDetails", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as PurchasingDetails[]).reduce((acc: Array<string>, curr: PurchasingDetails) => acc.concat(curr.validate()), new Array<string>());
            }},
            {field: "externalCodes", rule: (v: any) => !!v || "externalCodes is required"},
            {field: "externalCodes", rule: (v: any) => v.validate()},
            {field: "flatPricing", rule: (v: any) => (v === null) || v.validate()},
            {field: "labelling", rule: (v: any) => !!v || "labelling is required"},
            {field: "labelling", rule: (v: any) => v.validate()},
            {field: "materialDetails", rule: (v: any) => (v === null) || v.validate()},
            {field: "autoAssemblyBom", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as AutoAssemblyBomLine[]).reduce((acc: Array<string>, curr: AutoAssemblyBomLine) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class ExpectedDutyForItemResult extends ModelObject<ExpectedDutyForItemResult> {

    public itemId: Guid = Guid.createEmpty();
    public dutyCostUnrounded: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.itemId = Guid.parseOrEmpty(jsonSerializedObject.itemId);
          this.dutyCostUnrounded = jsonSerializedObject.dutyCostUnrounded;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.itemId = this.itemId.toString();
        toRet.dutyCostUnrounded = this.dutyCostUnrounded;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "itemId", rule: (v: any) => !v.isEmpty() || "ExpectedDutyForItemResult.ItemId cannot be empty"},
    ];
}


export class FilteredItems extends ModelObject<FilteredItems> {

    public matchingCount: number = 0;
    public items: Item[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.items = jsonSerializedObject.items.map((v: any) => new Item(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.items = this.items.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "items", rule: (v: any) => !!v || "items is required"},
            {field: "items", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as Item[]).reduce((acc: Array<string>, curr: Item) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class InvoiceableItem extends ModelObject<InvoiceableItem> {

    public details: Details = new Details();
    public invoicing: Invoicing = new Invoicing();
    public vat: Vat = new Vat();
    private _alcoholDuty: AlcoholDuty | null = null;
    public get alcoholDuty(): AlcoholDuty | null { return this._alcoholDuty }
    public set alcoholDuty(value: AlcoholDuty | null) {
      this._alcoholDuty = value === undefined ? null : value;
    }
    public externalCodes: ExternalCodes = new ExternalCodes();
    private _flatPricing: FlatPricing | null = null;
    public get flatPricing(): FlatPricing | null { return this._flatPricing }
    public set flatPricing(value: FlatPricing | null) {
      this._flatPricing = value === undefined ? null : value;
    }
    public packaging: Packaging = new Packaging();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.details = new Details(jsonSerializedObject.details);
          this.invoicing = new Invoicing(jsonSerializedObject.invoicing);
          this.vat = new Vat(jsonSerializedObject.vat);
          this.alcoholDuty = jsonSerializedObject.alcoholDuty == null ? null : new AlcoholDuty(jsonSerializedObject.alcoholDuty);
          this.externalCodes = new ExternalCodes(jsonSerializedObject.externalCodes);
          this.flatPricing = jsonSerializedObject.flatPricing == null ? null : new FlatPricing(jsonSerializedObject.flatPricing);
          this.packaging = new Packaging(jsonSerializedObject.packaging);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.details = this.details.toJsonSerializedObject();
        toRet.invoicing = this.invoicing.toJsonSerializedObject();
        toRet.vat = this.vat.toJsonSerializedObject();
        toRet.alcoholDuty = this.alcoholDuty == null ? null : this.alcoholDuty?.toJsonSerializedObject() ?? null;
        toRet.externalCodes = this.externalCodes.toJsonSerializedObject();
        toRet.flatPricing = this.flatPricing == null ? null : this.flatPricing?.toJsonSerializedObject() ?? null;
        toRet.packaging = this.packaging.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "details", rule: (v: any) => !!v || "details is required"},
            {field: "details", rule: (v: any) => v.validate()},
            {field: "invoicing", rule: (v: any) => !!v || "invoicing is required"},
            {field: "invoicing", rule: (v: any) => v.validate()},
            {field: "vat", rule: (v: any) => !!v || "vat is required"},
            {field: "vat", rule: (v: any) => v.validate()},
            {field: "alcoholDuty", rule: (v: any) => (v === null) || v.validate()},
            {field: "externalCodes", rule: (v: any) => !!v || "externalCodes is required"},
            {field: "externalCodes", rule: (v: any) => v.validate()},
            {field: "flatPricing", rule: (v: any) => (v === null) || v.validate()},
            {field: "packaging", rule: (v: any) => !!v || "packaging is required"},
            {field: "packaging", rule: (v: any) => v.validate()},
    ];
}


export class FilteredInvoiceableItems extends ModelObject<FilteredInvoiceableItems> {

    public matchingCount: number = 0;
    public invoiceableItems: InvoiceableItem[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.invoiceableItems = jsonSerializedObject.invoiceableItems.map((v: any) => new InvoiceableItem(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.invoiceableItems = this.invoiceableItems.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "invoiceableItems", rule: (v: any) => !!v || "invoiceableItems is required"},
            {field: "invoiceableItems", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as InvoiceableItem[]).reduce((acc: Array<string>, curr: InvoiceableItem) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class GlCodeOverrideForItem extends ModelObject<GlCodeOverrideForItem> {

    /**
    * Outlet Type ID
    */
    public outletTypeId: Guid = Guid.createEmpty();
    private _glCodeId: Guid | null = null;
    /**
    * Gl Code Id
    */
    public get glCodeId(): Guid | null { return this._glCodeId }
    public set glCodeId(value: Guid | null) {
      this._glCodeId = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.outletTypeId = Guid.parseOrEmpty(jsonSerializedObject.outletTypeId);
          this.glCodeId = jsonSerializedObject.glCodeId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.glCodeId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.outletTypeId = this.outletTypeId.toString();
        toRet.glCodeId = this.glCodeId == null ? null : this.glCodeId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "outletTypeId", rule: (v: any) => !v.isEmpty() || "GlCodeOverrideForItem.OutletTypeId cannot be empty"},
            {field: "glCodeId", rule: (v: any) => v == null || !v.isEmpty() || "GlCodeOverrideForItem.GlCodeId cannot be empty"},
    ];
}


export class StockTrackedItem extends ModelObject<StockTrackedItem> {

    public details: Details = new Details();
    public stock: Stock = new Stock();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.details = new Details(jsonSerializedObject.details);
          this.stock = new Stock(jsonSerializedObject.stock);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.details = this.details.toJsonSerializedObject();
        toRet.stock = this.stock.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "details", rule: (v: any) => !!v || "details is required"},
            {field: "details", rule: (v: any) => v.validate()},
            {field: "stock", rule: (v: any) => !!v || "stock is required"},
            {field: "stock", rule: (v: any) => v.validate()},
    ];
}


export class FilteredStockTrackedItems extends ModelObject<FilteredStockTrackedItems> {

    public matchingCount: number = 0;
    public stockTrackedItems: StockTrackedItem[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.stockTrackedItems = jsonSerializedObject.stockTrackedItems.map((v: any) => new StockTrackedItem(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.stockTrackedItems = this.stockTrackedItems.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "stockTrackedItems", rule: (v: any) => !!v || "stockTrackedItems is required"},
            {field: "stockTrackedItems", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as StockTrackedItem[]).reduce((acc: Array<string>, curr: StockTrackedItem) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class PurchasableItemAtLocation extends ModelObject<PurchasableItemAtLocation> {

    /**
    * Stock Item Id
    */
    public stockItemId: Guid = Guid.createEmpty();
    /**
    * Stock Location Id
    */
    public stockLocationId: Guid = Guid.createEmpty();
    private _reorderLevel: number | null = null;
    /**
    * The level at which users should re-order stock.
    * Not Negative
    */
    public get reorderLevel(): number | null { return this._reorderLevel }
    public set reorderLevel(value: number | null) {
      this._reorderLevel = value === undefined ? null : value;
    }
    private _reorderQuantity: number | null = null;
    /**
    * The default amount that the user should order.
    * Not Negative
    */
    public get reorderQuantity(): number | null { return this._reorderQuantity }
    public set reorderQuantity(value: number | null) {
      this._reorderQuantity = value === undefined ? null : value;
    }
    private _stockLevelAlert: number | null = null;
    /**
    * The level at which the user should be notified that more stock is required.
    * Not Negative
    */
    public get stockLevelAlert(): number | null { return this._stockLevelAlert }
    public set stockLevelAlert(value: number | null) {
      this._stockLevelAlert = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.stockLocationId = Guid.parseOrEmpty(jsonSerializedObject.stockLocationId);
          this.reorderLevel = jsonSerializedObject.reorderLevel == null ? null : jsonSerializedObject.reorderLevel;
          this.reorderQuantity = jsonSerializedObject.reorderQuantity == null ? null : jsonSerializedObject.reorderQuantity;
          this.stockLevelAlert = jsonSerializedObject.stockLevelAlert == null ? null : jsonSerializedObject.stockLevelAlert;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.stockItemId = this.stockItemId.toString();
        toRet.stockLocationId = this.stockLocationId.toString();
        toRet.reorderLevel = this.reorderLevel == null ? null : this.reorderLevel;
        toRet.reorderQuantity = this.reorderQuantity == null ? null : this.reorderQuantity;
        toRet.stockLevelAlert = this.stockLevelAlert == null ? null : this.stockLevelAlert;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "PurchasableItemAtLocation.StockItemId cannot be empty"},
            {field: "stockLocationId", rule: (v: any) => !v.isEmpty() || "PurchasableItemAtLocation.StockLocationId cannot be empty"},
    ];
}


export class DeliveryServiceItem extends BaseModelObject<DeliveryServiceItem> {

    public details: Details = new Details();
    public invoicing: Invoicing = new Invoicing();
    public vat: Vat = new Vat();
    public externalCodes: ExternalCodes = new ExternalCodes();
    public flatPricing: FlatPricing = new FlatPricing();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.details = new Details(jsonSerializedObject.details);
          this.invoicing = new Invoicing(jsonSerializedObject.invoicing);
          this.vat = new Vat(jsonSerializedObject.vat);
          this.externalCodes = new ExternalCodes(jsonSerializedObject.externalCodes);
          this.flatPricing = new FlatPricing(jsonSerializedObject.flatPricing);
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.details = this.details.toJsonSerializedObject();
        toRet.invoicing = this.invoicing.toJsonSerializedObject();
        toRet.vat = this.vat.toJsonSerializedObject();
        toRet.externalCodes = this.externalCodes.toJsonSerializedObject();
        toRet.flatPricing = this.flatPricing.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "details", rule: (v: any) => !!v || "details is required"},
            {field: "details", rule: (v: any) => v.validate()},
            {field: "invoicing", rule: (v: any) => !!v || "invoicing is required"},
            {field: "invoicing", rule: (v: any) => v.validate()},
            {field: "vat", rule: (v: any) => !!v || "vat is required"},
            {field: "vat", rule: (v: any) => v.validate()},
            {field: "externalCodes", rule: (v: any) => !!v || "externalCodes is required"},
            {field: "externalCodes", rule: (v: any) => v.validate()},
            {field: "flatPricing", rule: (v: any) => !!v || "flatPricing is required"},
            {field: "flatPricing", rule: (v: any) => v.validate()},
    ];
}


export class BottleDepositChargeItem extends BaseModelObject<BottleDepositChargeItem> {

    public details: Details = new Details();
    public invoicing: Invoicing = new Invoicing();
    public vat: Vat = new Vat();
    public externalCodes: ExternalCodes = new ExternalCodes();
    public flatPricing: FlatPricing = new FlatPricing();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.details = new Details(jsonSerializedObject.details);
          this.invoicing = new Invoicing(jsonSerializedObject.invoicing);
          this.vat = new Vat(jsonSerializedObject.vat);
          this.externalCodes = new ExternalCodes(jsonSerializedObject.externalCodes);
          this.flatPricing = new FlatPricing(jsonSerializedObject.flatPricing);
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.details = this.details.toJsonSerializedObject();
        toRet.invoicing = this.invoicing.toJsonSerializedObject();
        toRet.vat = this.vat.toJsonSerializedObject();
        toRet.externalCodes = this.externalCodes.toJsonSerializedObject();
        toRet.flatPricing = this.flatPricing.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "details", rule: (v: any) => !!v || "details is required"},
            {field: "details", rule: (v: any) => v.validate()},
            {field: "invoicing", rule: (v: any) => !!v || "invoicing is required"},
            {field: "invoicing", rule: (v: any) => v.validate()},
            {field: "vat", rule: (v: any) => !!v || "vat is required"},
            {field: "vat", rule: (v: any) => v.validate()},
            {field: "externalCodes", rule: (v: any) => !!v || "externalCodes is required"},
            {field: "externalCodes", rule: (v: any) => v.validate()},
            {field: "flatPricing", rule: (v: any) => !!v || "flatPricing is required"},
            {field: "flatPricing", rule: (v: any) => v.validate()},
    ];
}


export class BottleDepositRedemptionItem extends BaseModelObject<BottleDepositRedemptionItem> {

    public details: Details = new Details();
    public invoicing: Invoicing = new Invoicing();
    public vat: Vat = new Vat();
    public externalCodes: ExternalCodes = new ExternalCodes();
    /**
    * FlatPricing required to avoid appearing on price lists. Actual price within is typically ignored as the redemption is typically based on the available balance. So set contents to 0.00 Ex Vat for convenience.
    */
    public flatPricing: FlatPricing = new FlatPricing();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.details = new Details(jsonSerializedObject.details);
          this.invoicing = new Invoicing(jsonSerializedObject.invoicing);
          this.vat = new Vat(jsonSerializedObject.vat);
          this.externalCodes = new ExternalCodes(jsonSerializedObject.externalCodes);
          this.flatPricing = new FlatPricing(jsonSerializedObject.flatPricing);
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.details = this.details.toJsonSerializedObject();
        toRet.invoicing = this.invoicing.toJsonSerializedObject();
        toRet.vat = this.vat.toJsonSerializedObject();
        toRet.externalCodes = this.externalCodes.toJsonSerializedObject();
        toRet.flatPricing = this.flatPricing.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "details", rule: (v: any) => !!v || "details is required"},
            {field: "details", rule: (v: any) => v.validate()},
            {field: "invoicing", rule: (v: any) => !!v || "invoicing is required"},
            {field: "invoicing", rule: (v: any) => v.validate()},
            {field: "vat", rule: (v: any) => !!v || "vat is required"},
            {field: "vat", rule: (v: any) => v.validate()},
            {field: "externalCodes", rule: (v: any) => !!v || "externalCodes is required"},
            {field: "externalCodes", rule: (v: any) => v.validate()},
            {field: "flatPricing", rule: (v: any) => !!v || "flatPricing is required"},
            {field: "flatPricing", rule: (v: any) => v.validate()},
    ];
}


export class AutoAssembledItem extends ModelObject<AutoAssembledItem> {

    public details: Details = new Details();
    public invoicing: Invoicing = new Invoicing();
    public vat: Vat = new Vat();
    private _alcoholDuty: AlcoholDuty | null = null;
    public get alcoholDuty(): AlcoholDuty | null { return this._alcoholDuty }
    public set alcoholDuty(value: AlcoholDuty | null) {
      this._alcoholDuty = value === undefined ? null : value;
    }
    public externalCodes: ExternalCodes = new ExternalCodes();
    private _flatPricing: FlatPricing | null = null;
    public get flatPricing(): FlatPricing | null { return this._flatPricing }
    public set flatPricing(value: FlatPricing | null) {
      this._flatPricing = value === undefined ? null : value;
    }
    public packaging: Packaging = new Packaging();
    public bom: AutoAssemblyBomLine[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.details = new Details(jsonSerializedObject.details);
          this.invoicing = new Invoicing(jsonSerializedObject.invoicing);
          this.vat = new Vat(jsonSerializedObject.vat);
          this.alcoholDuty = jsonSerializedObject.alcoholDuty == null ? null : new AlcoholDuty(jsonSerializedObject.alcoholDuty);
          this.externalCodes = new ExternalCodes(jsonSerializedObject.externalCodes);
          this.flatPricing = jsonSerializedObject.flatPricing == null ? null : new FlatPricing(jsonSerializedObject.flatPricing);
          this.packaging = new Packaging(jsonSerializedObject.packaging);
          this.bom = jsonSerializedObject.bom.map((v: any) => new AutoAssemblyBomLine(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.details = this.details.toJsonSerializedObject();
        toRet.invoicing = this.invoicing.toJsonSerializedObject();
        toRet.vat = this.vat.toJsonSerializedObject();
        toRet.alcoholDuty = this.alcoholDuty == null ? null : this.alcoholDuty?.toJsonSerializedObject() ?? null;
        toRet.externalCodes = this.externalCodes.toJsonSerializedObject();
        toRet.flatPricing = this.flatPricing == null ? null : this.flatPricing?.toJsonSerializedObject() ?? null;
        toRet.packaging = this.packaging.toJsonSerializedObject();
        toRet.bom = this.bom.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "details", rule: (v: any) => !!v || "details is required"},
            {field: "details", rule: (v: any) => v.validate()},
            {field: "invoicing", rule: (v: any) => !!v || "invoicing is required"},
            {field: "invoicing", rule: (v: any) => v.validate()},
            {field: "vat", rule: (v: any) => !!v || "vat is required"},
            {field: "vat", rule: (v: any) => v.validate()},
            {field: "alcoholDuty", rule: (v: any) => (v === null) || v.validate()},
            {field: "externalCodes", rule: (v: any) => !!v || "externalCodes is required"},
            {field: "externalCodes", rule: (v: any) => v.validate()},
            {field: "flatPricing", rule: (v: any) => (v === null) || v.validate()},
            {field: "packaging", rule: (v: any) => !!v || "packaging is required"},
            {field: "packaging", rule: (v: any) => v.validate()},
            {field: "bom", rule: (v: any) => !!v || "bom is required"},
            {field: "bom", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as AutoAssemblyBomLine[]).reduce((acc: Array<string>, curr: AutoAssemblyBomLine) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class FilteredAutoAssembledItems extends ModelObject<FilteredAutoAssembledItems> {

    public matchingCount: number = 0;
    public autoAssembledItems: AutoAssembledItem[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.autoAssembledItems = jsonSerializedObject.autoAssembledItems.map((v: any) => new AutoAssembledItem(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.autoAssembledItems = this.autoAssembledItems.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "autoAssembledItems", rule: (v: any) => !!v || "autoAssembledItems is required"},
            {field: "autoAssembledItems", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as AutoAssembledItem[]).reduce((acc: Array<string>, curr: AutoAssembledItem) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class PoDeliveryServiceItem extends BaseModelObject<PoDeliveryServiceItem> {

    public details: Details = new Details();
    public vat: Vat = new Vat();
    /**
    * Purchasing details are required in order to appear on a purchase order but some details may not be relevant (Eg price is likely to always be overwritten) and should be set to sensible defaults - Eg 0.
    */
    public purchasingDetails: PurchasingDetails[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.details = new Details(jsonSerializedObject.details);
          this.vat = new Vat(jsonSerializedObject.vat);
          this.purchasingDetails = jsonSerializedObject.purchasingDetails.map((v: any) => new PurchasingDetails(v));
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.details = this.details.toJsonSerializedObject();
        toRet.vat = this.vat.toJsonSerializedObject();
        toRet.purchasingDetails = this.purchasingDetails.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "details", rule: (v: any) => !!v || "details is required"},
            {field: "details", rule: (v: any) => v.validate()},
            {field: "vat", rule: (v: any) => !!v || "vat is required"},
            {field: "vat", rule: (v: any) => v.validate()},
            {field: "purchasingDetails", rule: (v: any) => !!v || "purchasingDetails is required"},
            {field: "purchasingDetails", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as PurchasingDetails[]).reduce((acc: Array<string>, curr: PurchasingDetails) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class ItemService extends ServiceBase {

  public async generateItemCode(tenantId: Guid, name: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<string> {
    const req = {
    tenantId:tenantId.toString(),
    name:name,
    };
    const extractor = (response:any) => response.code;;
    return this.makeJsonRequest("item/v1/generateItemCode", req, false, extractor, opts);
  };

  public async getItem(tenantId: Guid, itemId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Item> {
    const req = {
    tenantId:tenantId.toString(),
    itemId:itemId.toString(),
    };
    const extractor = (response:any) => new Item(response.item);;
    return this.makeJsonRequest("item/v1/getItem", req, false, extractor, opts);
  };

  public async getItems(tenantId: Guid, itemIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Item[]> {
    const req = {
    tenantId:tenantId.toString(),
    itemIds:itemIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.items.map((v: any) => new Item(v));;
    return this.makeJsonRequest("item/v1/getItems", req, false, extractor, opts);
  };

  public async getAllItems(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Item[]> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = (response:any) => response.items.map((v: any) => new Item(v));;
    return this.makeJsonRequest("item/v1/getAllItems", req, false, extractor, opts);
  };

  public async setItemIsHidden(tenantId: Guid, itemId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    itemId:itemId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("item/v1/setItemIsHidden", req, true, extractor, opts);
  };

  public async evaluateExpectedDutyForItems(tenantId: Guid, itemIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<ExpectedDutyForItemResult[]> {
    const req = {
    tenantId:tenantId.toString(),
    itemIds:itemIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.expectedDutyForItemResult.map((v: any) => new ExpectedDutyForItemResult(v));;
    return this.makeJsonRequest("item/v1/evaluateExpectedDutyForItems", req, false, extractor, opts);
  };

  public async saveItem(tenantId: Guid, item: Item, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    item:item.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("item/v1/saveItem", req, true, extractor, opts);
  };

  public async getItemsByFilter(tenantId: Guid, filter: ItemFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredItems> {
    const req = {
    tenantId:tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredItems(response.results);;
    return this.makeJsonRequest("item/v1/getItemsByFilter", req, false, extractor, opts);
  };

  public async getInvoiceableItem(tenantId: Guid, itemId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<InvoiceableItem> {
    const req = {
    tenantId:tenantId.toString(),
    itemId:itemId.toString(),
    };
    const extractor = (response:any) => new InvoiceableItem(response.invoiceableItem);;
    return this.makeJsonRequest("item/v1/getInvoiceableItem", req, false, extractor, opts);
  };

  public async getInvoiceableItems(tenantId: Guid, itemIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<InvoiceableItem[]> {
    const req = {
    tenantId:tenantId.toString(),
    itemIds:itemIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.invoiceableItems.map((v: any) => new InvoiceableItem(v));;
    return this.makeJsonRequest("item/v1/getInvoiceableItems", req, false, extractor, opts);
  };

  public async getAllInvoiceableItems(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<InvoiceableItem[]> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = (response:any) => response.invoiceableItems.map((v: any) => new InvoiceableItem(v));;
    return this.makeJsonRequest("item/v1/getAllInvoiceableItems", req, false, extractor, opts);
  };

  public async getInvoiceableItemsByFilter(tenantId: Guid, filter: ItemFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredInvoiceableItems> {
    const req = {
    tenantId:tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredInvoiceableItems(response.results);;
    return this.makeJsonRequest("item/v1/getInvoiceableItemsByFilter", req, false, extractor, opts);
  };

  public async getInvoiceableItemsWhichCurrentlyAllowSalesByFilter(tenantId: Guid, filter: ItemFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredInvoiceableItems> {
    const req = {
    tenantId:tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredInvoiceableItems(response.results);;
    return this.makeJsonRequest("item/v1/getInvoiceableItemsWhichCurrentlyAllowSalesByFilter", req, false, extractor, opts);
  };

  public async getGlCodeOverridesForInvoiceableItem(tenantId: Guid, invoiceableItemId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<GlCodeOverrideForItem[]> {
    const req = {
    tenantId:tenantId.toString(),
    invoiceableItemId:invoiceableItemId.toString(),
    };
    const extractor = (response:any) => response.glCodeOverrides.map((v: any) => new GlCodeOverrideForItem(v));;
    return this.makeJsonRequest("item/v1/getGlCodeOverridesForInvoiceableItem", req, false, extractor, opts);
  };

  public async updateInvoiceableItemGlCodeOverrides(tenantId: Guid, invoiceableItemId: Guid, expectedVersion: number | null, glCodeOverrides: readonly GlCodeOverrideForItem[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    invoiceableItemId:invoiceableItemId.toString(),
    expectedVersion:expectedVersion == null ? null : expectedVersion,
    glCodeOverrides:glCodeOverrides.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("item/v1/updateInvoiceableItemGlCodeOverrides", req, true, extractor, opts);
  };

  public async getStockTrackedItemsByFilter(tenantId: Guid, filter: ItemFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredStockTrackedItems> {
    const req = {
    tenantId:tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredStockTrackedItems(response.results);;
    return this.makeJsonRequest("item/v1/getStockTrackedItemsByFilter", req, false, extractor, opts);
  };

  public async savePurchasableItemAtLocation(tenantId: Guid, purchasableItemAtLocation: PurchasableItemAtLocation, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    purchasableItemAtLocation:purchasableItemAtLocation.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("item/v1/savePurchasableItemAtLocation", req, true, extractor, opts);
  };

  public async getPurchasableItemAtLocations(tenantId: Guid, stockItemIds: readonly Guid[] | null, stockLocationIds: readonly Guid[] | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<PurchasableItemAtLocation[]> {
    const req = {
    tenantId:tenantId.toString(),
    stockItemIds:stockItemIds == null ? null : stockItemIds.map(v => v.toString()),
    stockLocationIds:stockLocationIds == null ? null : stockLocationIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.purchasableItemAtLocations.map((v: any) => new PurchasableItemAtLocation(v));;
    return this.makeJsonRequest("item/v1/getPurchasableItemAtLocations", req, false, extractor, opts);
  };

  public async getAllDeliveryServiceItems(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<DeliveryServiceItem[]> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = (response:any) => response.deliveryServiceItem.map((v: any) => new DeliveryServiceItem(v));;
    return this.makeJsonRequest("item/v1/getAllDeliveryServiceItems", req, false, extractor, opts);
  };

  public async getDeliveryServiceItem(tenantId: Guid, itemId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<DeliveryServiceItem> {
    const req = {
    tenantId:tenantId.toString(),
    itemId:itemId.toString(),
    };
    const extractor = (response:any) => new DeliveryServiceItem(response.deliveryServiceItem);;
    return this.makeJsonRequest("item/v1/getDeliveryServiceItem", req, false, extractor, opts);
  };

  public async saveDeliveryServiceItem(tenantId: Guid, deliveryServiceItem: DeliveryServiceItem, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    deliveryServiceItem:deliveryServiceItem.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("item/v1/saveDeliveryServiceItem", req, true, extractor, opts);
  };

  public async getAllBottleDepositChargeItems(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<BottleDepositChargeItem[]> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = (response:any) => response.items.map((v: any) => new BottleDepositChargeItem(v));;
    return this.makeJsonRequest("item/v1/getAllBottleDepositChargeItems", req, false, extractor, opts);
  };

  public async getBottleDepositChargeItem(tenantId: Guid, itemId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<BottleDepositChargeItem> {
    const req = {
    tenantId:tenantId.toString(),
    itemId:itemId.toString(),
    };
    const extractor = (response:any) => new BottleDepositChargeItem(response.item);;
    return this.makeJsonRequest("item/v1/getBottleDepositChargeItem", req, false, extractor, opts);
  };

  public async getBottleDepositChargeItems(tenantId: Guid, itemIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<BottleDepositChargeItem[]> {
    const req = {
    tenantId:tenantId.toString(),
    itemIds:itemIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.items.map((v: any) => new BottleDepositChargeItem(v));;
    return this.makeJsonRequest("item/v1/getBottleDepositChargeItems", req, false, extractor, opts);
  };

  public async saveBottleDepositChargeItem(tenantId: Guid, item: BottleDepositChargeItem, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    item:item.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("item/v1/saveBottleDepositChargeItem", req, true, extractor, opts);
  };

  public async getBottleDepositRedemptionItem(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<BottleDepositRedemptionItem | null> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = (response:any) => response.item == null ? null : new BottleDepositRedemptionItem(response.item);;
    return this.makeJsonRequest("item/v1/getBottleDepositRedemptionItem", req, false, extractor, opts);
  };

  public async saveBottleDepositRedemptionItem(tenantId: Guid, item: BottleDepositRedemptionItem, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    item:item.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("item/v1/saveBottleDepositRedemptionItem", req, true, extractor, opts);
  };

  public async getAutoAssembledItem(tenantId: Guid, itemId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<AutoAssembledItem> {
    const req = {
    tenantId:tenantId.toString(),
    itemId:itemId.toString(),
    };
    const extractor = (response:any) => new AutoAssembledItem(response.autoAssembledItem);;
    return this.makeJsonRequest("item/v1/getAutoAssembledItem", req, false, extractor, opts);
  };

  public async getAutoAssembledItems(tenantId: Guid, itemIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<AutoAssembledItem[]> {
    const req = {
    tenantId:tenantId.toString(),
    itemIds:itemIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.autoAssembledItems.map((v: any) => new AutoAssembledItem(v));;
    return this.makeJsonRequest("item/v1/getAutoAssembledItems", req, false, extractor, opts);
  };

  public async getAllAutoAssembledItems(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<AutoAssembledItem[]> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = (response:any) => response.autoAssembledItems.map((v: any) => new AutoAssembledItem(v));;
    return this.makeJsonRequest("item/v1/getAllAutoAssembledItems", req, false, extractor, opts);
  };

  public async saveAutoAssembledItem(tenantId: Guid, autoAssembledItem: AutoAssembledItem, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    autoAssembledItem:autoAssembledItem.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("item/v1/saveAutoAssembledItem", req, true, extractor, opts);
  };

  public async getAutoAssembledItemsByFilter(tenantId: Guid, filter: ItemFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredAutoAssembledItems> {
    const req = {
    tenantId:tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredAutoAssembledItems(response.results);;
    return this.makeJsonRequest("item/v1/getAutoAssembledItemsByFilter", req, false, extractor, opts);
  };

  public async getAutoAssembledItemsWhichContainGivenItems(tenantId: Guid, childItemIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<AutoAssembledItem[]> {
    const req = {
    tenantId:tenantId.toString(),
    childItemIds:childItemIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.autoAssembledItems.map((v: any) => new AutoAssembledItem(v));;
    return this.makeJsonRequest("item/v1/getAutoAssembledItemsWhichContainGivenItems", req, false, extractor, opts);
  };

  public async getPoDeliveryServiceItem(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<PoDeliveryServiceItem | null> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = (response:any) => response.item == null ? null : new PoDeliveryServiceItem(response.item);;
    return this.makeJsonRequest("item/v1/getPoDeliveryServiceItem", req, false, extractor, opts);
  };

  public async savePoDeliveryServiceItem(tenantId: Guid, item: PoDeliveryServiceItem, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    item:item.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("item/v1/savePoDeliveryServiceItem", req, true, extractor, opts);
  };
}

export const itemService = new ItemService();